import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { colorOf } from '@/components/ScoreRing';
import { TimeField } from '@/components/TimeField';
import { ChevronLeft, Moon, Sunrise, Lightbulb, Check } from 'lucide-react';
import { format } from 'date-fns';

export default function SleepLog() {
  const nav = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [bed, setBed] = useState('22:45');
  const [wake, setWake] = useState('06:10');
  const [quality, setQuality] = useState(8);
  const [log, setLog] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/sleep-logs', { params: { date: today } }).then(r => {
      if (r.data) { setLog(r.data); setBed(r.data.bed_time); setWake(r.data.wake_time); setQuality(r.data.quality); }
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post('/sleep-logs', { date: today, bed_time: bed, wake_time: wake, quality });
      setLog(r.data); toast.success('Sleep saved'); nav(-1);
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const qLabels = ['', 'Terrible', 'Very Poor', 'Poor', 'Below Avg', 'Average', 'Decent', 'Good', 'Very Good', 'Great', 'Perfect'];

  return (
    <div className="space-y-5" data-testid="sleep-log-page">
      <Header title="Sleep Log" onBack={() => nav(-1)} />
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#A78BFA]/15 flex items-center justify-center"><Moon className="w-5 h-5 text-[#A78BFA]" /></div>
        <div><div className="font-display font-bold">Last Night</div><div className="text-xs text-muted-foreground">{format(new Date(), 'EEEE, MMM d')}</div></div>
      </div>

      <div className="regen-card-elevated p-5 space-y-4">
        <div className="font-display font-bold">1. Enter Your Sleep</div>
        <div className="grid grid-cols-2 gap-4">
          <TimeField icon={Moon} label="Bed Time" value={bed} onChange={setBed} testid="sleep-bed-time" />
          <TimeField icon={Sunrise} label="Wake Up Time" value={wake} onChange={setWake} testid="sleep-wake-time" />
        </div>
        <div>
          <div className="text-sm font-semibold mb-2">Sleep Quality <span className="text-[#FF5C5C]">*</span></div>
          <div className="grid grid-cols-10 gap-1.5">
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} onClick={() => setQuality(n)} data-testid={`sleep-quality-${n}`}
                className={`aspect-square rounded-full text-xs font-semibold transition ${quality === n ? 'bg-[#A78BFA] text-[var(--c-ink)] scale-110' : 'bg-[var(--c-film)] text-muted-foreground'}`}>{n}</button>
            ))}
          </div>
          <div className="text-center text-xs text-[#A78BFA] font-semibold mt-2">{qLabels[quality]}</div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-sleep-btn" className="w-full rounded-xl py-3 bg-[#A78BFA] text-[var(--c-ink)] font-bold tracking-wide active:scale-[0.99] transition disabled:opacity-60">
          {saving ? 'SAVING…' : 'SAVE SLEEP'}
        </button>
      </div>

      {log && (
        <div className="regen-card-elevated p-5" data-testid="sleep-summary">
          <div className="font-display font-bold mb-3">Sleep Summary</div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <Stat label="Time in Bed" value={log.time_in_bed} sub="Goal: 7h+" subColor={colorOf(log.duration_color)} />
            <Stat label="Sleep Score" value={log.sleep_score} sub={log.score_label} subColor='#65A30D' />
            <Stat label="Goal" value={log.goal_achieved ? 'Yes' : 'No'} valColor={log.goal_achieved ? '#65A30D' : '#FF5C5C'} sub={log.goal_achieved ? '✓ Achieved' : 'Keep going'} subColor={log.goal_achieved ? '#65A30D' : '#FFB800'} icon={log.goal_achieved ? Check : null} />
          </div>
        </div>
      )}

      <div className="regen-card-elevated p-4 flex items-start gap-3">
        <Lightbulb className="w-5 h-5 text-[#A78BFA] flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-[#A78BFA]">Sleep Tip</div>
          <div className="text-sm text-muted-foreground mt-0.5">Bedtime matters as much as duration — going to bed before 10:30 PM aligns with your circadian rhythm and boosts recovery, even at the same hours of sleep.</div>
        </div>
      </div>
    </div>
  );
}

function Header({ title, onBack }) {
  return (
    <div className="flex items-center gap-3">
      <button onClick={onBack} data-testid="back-btn" className="w-9 h-9 rounded-full bg-[var(--c-film)] flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
      <h1 className="font-display font-black text-2xl">{title}</h1>
    </div>
  );
}

function Stat({ label, value, sub, subColor, valColor, icon: Icon }) {
  return (
    <div>
      <div className="label-mini">{label}</div>
      <div className="font-mono text-2xl mt-1 flex items-center justify-center gap-1" style={valColor ? { color: valColor } : {}}>
        {Icon && <Icon className="w-5 h-5" />}{value}
      </div>
      {sub && <div className="text-xs font-semibold mt-0.5" style={{ color: subColor }}>{sub}</div>}
    </div>
  );
}
